import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, XCircle, Clock, Brain } from "lucide-react";
import {
  compilePersonalityPrompt,
  type AgentPersonality,
  type ConversationType,
} from "@/lib/personality-compiler";

interface TraceStep {
  id: string;
  step: string;
  edge_function: string | null;
  started_at: string;
  duration_ms: number | null;
  ok: boolean;
  payload: Record<string, unknown> | null;
  error: string | null;
}

interface PersonaContext {
  block: string;
  convType: ConversationType;
  source: AgentPersonality;
  agentName: string | null;
  groupMode: string | null;
}

export default function TurnInspector() {
  const { id } = useParams<{ id: string }>();
  const [steps, setSteps] = useState<TraceStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState<PersonaContext | null>(null);
  const [personaError, setPersonaError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("turn_trace")
        .select("id, step, edge_function, started_at, duration_ms, ok, payload, error, org_id, group_id")
        .eq("turn_id", id)
        .order("started_at", { ascending: true });
      if (cancelled) return;
      if (!error && data) setSteps(data as unknown as TraceStep[]);
      setLoading(false);

      // MS-03: derive the *exact* compiled persona block for this turn,
      // mirroring what the edge `personality-compiler.ts` would produce.
      try {
        const first = (data as Array<{ org_id: string; group_id: string | null }> | null)?.[0];
        if (!first) return;
        const groupId = first.group_id;
        const orgId = first.org_id;

        // Find the agent for this group (or org default).
        let agentQuery = supabase
          .from("syra_ai_agents_safe")
          .select("id, name, group_id, org_id")
          .eq("active", true)
          .limit(1);
        agentQuery = groupId
          ? agentQuery.eq("group_id", groupId)
          : agentQuery.eq("org_id", orgId).is("group_id", null);
        const { data: agentRow } = await agentQuery.maybeSingle();

        let agentId = agentRow?.id ?? null;
        if (!agentId) {
          const { data: fallback } = await supabase
            .from("syra_ai_agents_safe")
            .select("id, name")
            .eq("org_id", orgId)
            .eq("active", true)
            .limit(1)
            .maybeSingle();
          agentId = fallback?.id ?? null;
        }
        if (!agentId) return;

        const [{ data: pers }, { data: gp }] = await Promise.all([
          supabase.from("agent_personality").select("*").eq("agent_id", agentId).maybeSingle(),
          groupId
            ? supabase.from("group_profiles").select("mode").eq("group_id", groupId).maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        if (!pers || cancelled) return;

        const convType: ConversationType = groupId ? "group" : "direct";
        const block = compilePersonalityPrompt(pers as AgentPersonality, convType);
        setPersona({
          block,
          convType,
          source: pers as AgentPersonality,
          agentName: agentRow?.name ?? null,
          groupMode: (gp as { mode?: string } | null)?.mode ?? null,
        });
      } catch (e) {
        setPersonaError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const totalMs = steps.reduce((acc, s) => acc + (s.duration_ms ?? 0), 0);
  const failed = steps.filter((s) => !s.ok).length;

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/cortex-os"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Inspector de Turno</h1>
          <p className="text-sm text-muted-foreground font-mono">{id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Etapas registradas</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-semibold">{steps.length}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Latência total</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-semibold">{totalMs}ms</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Falhas</CardTitle></CardHeader>
          <CardContent><p className={`text-3xl font-semibold ${failed > 0 ? "text-destructive" : "text-foreground"}`}>{failed}</p></CardContent>
        </Card>
      </div>

      {/* MS-03 — Persona Trace */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            Persona compilada (system prompt anexado)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {persona ? (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                {persona.agentName && <Badge variant="outline">Agente: {persona.agentName}</Badge>}
                <Badge variant="secondary">Contexto: {persona.convType}</Badge>
                {persona.groupMode && <Badge variant="secondary">Group mode: {persona.groupMode}</Badge>}
                <Badge variant="outline">freq: {persona.source.response_frequency}</Badge>
                <Badge variant="outline">formal: {persona.source.formality}</Badge>
                <Badge variant="outline">proat: {persona.source.proactivity}</Badge>
                <Badge variant="outline">cautela: {persona.source.caution_level}</Badge>
              </div>
              <pre className="text-xs whitespace-pre-wrap bg-muted/40 rounded p-3 border border-border">
{persona.block}
              </pre>
              <p className="text-xs text-muted-foreground">
                Reproduzido determinísticamente via{" "}
                <code className="font-mono">compilePersonalityPrompt</code> com a mesma lógica do
                edge <code className="font-mono">_shared/personality-compiler.ts</code>.
              </p>
            </>
          ) : personaError ? (
            <p className="text-sm text-destructive">Erro ao carregar persona: {personaError}</p>
          ) : loading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Persona não localizada para este turno (sem agent_personality associado ao grupo/org).
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Timeline</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : steps.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum passo encontrado para este turno.</p>
          ) : (
            <div className="space-y-3">
              {steps.map((s) => (
                <div key={s.id} className="flex gap-3 items-start border-l-2 border-border pl-4 py-2">
                  <div className="mt-1">
                    {s.ok ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <XCircle className="h-4 w-4 text-destructive" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium font-mono text-sm">{s.step}</span>
                      {s.edge_function && <Badge variant="outline" className="text-xs">{s.edge_function}</Badge>}
                      <Badge variant="secondary" className="text-xs"><Clock className="h-3 w-3 mr-1" />{s.duration_ms ?? 0}ms</Badge>
                    </div>
                    {s.error && <p className="text-xs text-destructive mt-1">{s.error}</p>}
                    {s.payload && Object.keys(s.payload).length > 0 && (
                      <pre className="text-xs text-muted-foreground mt-1 overflow-x-auto bg-muted/30 rounded p-2">
                        {JSON.stringify(s.payload, null, 2)}
                      </pre>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(s.started_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
